import { useTranslation } from "react-i18next";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Trophy,
  Gift,
  Crown,
  RefreshCw,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Money } from "@/components/common/Money";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { TransactionRow as Row, TransactionType } from "@/types/database";

const ICONS: Record<TransactionType, LucideIcon> = {
  deposit: ArrowDownLeft,
  withdraw: ArrowUpRight,
  winning: Trophy,
  bonus: Gift,
  subscription: Crown,
  refund: RefreshCw,
  transfer_in: Users,
  transfer_out: Users,
  admin_adjust: RefreshCw,
};

export function TransactionRow({ tx }: { tx: Row }) {
  const { t, i18n } = useTranslation();
  const Icon = ICONS[tx.type] ?? ArrowDownLeft;
  const isCredit = ["deposit", "winning", "bonus", "refund", "transfer_in"].includes(tx.type);
  const signed = isCredit ? tx.amount : -tx.amount;
  const status = tx.status;

  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02] transition-colors">
      <div className={cn(
        "size-8 rounded-md flex items-center justify-center shrink-0",
        isCredit ? "bg-success/10 text-success" : "bg-white/[0.04] text-muted-foreground"
      )}>
        <Icon className="size-4" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">
          {t(`wallet.txTypes.${tx.type}`, { defaultValue: tx.type })}
        </p>
        <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
          <span>{formatRelative(tx.created_at, i18n.language as "fr" | "en")}</span>
          {status !== "success" && (
            <>
              <span className="text-muted-foreground/40">·</span>
              <span className={cn(
                status === "pending" || status === "processing"
                  ? "text-warning"
                  : status === "failed"
                  ? "text-destructive"
                  : "text-muted-foreground"
              )}>
                {t(`wallet.txStatus.${status}`)}
              </span>
            </>
          )}
        </div>
      </div>
      <Money amount={signed} signed className="text-sm font-semibold num" />
    </div>
  );
}
